import { isDefined, isNotDefined } from '@sgme/fp';
import { useContext } from 'react';
import { useIntl } from 'react-intl';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import { OrderRow } from '@/App/tiles/orders/components/OrderRow.tsx';
import {
  FixingContext,
  useFixingSetIsModifyingField,
  useValidateFixing,
} from '@/App/tiles/orders/FixingOrder/FixingContext.tsx';
import { SgDatePicker } from '@/components/sg-design-system/components/input/SgDatePicker/index.tsx';
import { OmsFieldErrorWrapper } from '@/components/ui/OmsFieldErrorWrapper.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import { selectFixingDeliveryDate, selectFixingErrors } from '@/store/orders/fixing/selectors.ts';
import { getUserPreferences } from '@/store/userPreferences/slice.ts';
import { DATE_FORMAT_TEXT } from '@/utils/date.ts';

export const FixingDeliveryDate = () => {
  const tileId = useContext(FixingContext).tileId;
  const setIsModifyingField = useFixingSetIsModifyingField();
  const track = useTrackEvent();

  const fixingDeliveryDate = useAppSelector((state) => selectFixingDeliveryDate(state, tileId));
  const errors = useAppSelector((state) => selectFixingErrors(state, tileId));
  const { locale } = useIntl();
  const { dateInputCultureInfo } = useAppSelector(getUserPreferences);

  const validateFixing = useValidateFixing();

  const handleDeliveryDateChange = (date: string) => {
    track('Order action', 'Delivery date changed');
    if (isNotDefined(date)) {
      return;
    }

    validateFixing({
      expiryDay: date,
    });
  };

  const getComputedLocale = () => {
    if (dateInputCultureInfo === 'en') {
      return 'en-US';
    }
    if (dateInputCultureInfo === 'fr') {
      return 'fr-FR';
    }
    if (locale === 'en') {
      return 'en-GB';
    }
    return 'fr-FR';
  };

  const deliveryDateError = errors?.expiryDay;

  return (
    <OrderRow labelId="order.fields.deliveryDate" alignTop>
      <div
        className="d-flex flex-column flex-nowrap  gap-4px align-items-between justify-content-between flex-grow-0 flex-shrink-0"
        data-e2e="fixing-delivery-date"
      >
        <OmsFieldErrorWrapper errors={deliveryDateError}>
          <SgDatePicker
            size="sm"
            yearControls
            value={fixingDeliveryDate}
            placeholder="Delivery date"
            onChange={handleDeliveryDateChange}
            locale={locale === 'en' ? 'en-GB' : 'fr-FR'}
            dateInputCultureInfo={getComputedLocale()}
            showClearButton={false}
            onFocus={() => setIsModifyingField(true)}
            onBlurInput={() => setIsModifyingField(false)}
            validationState={isDefined(deliveryDateError) ? 'invalid' : undefined}
            disablePresets={{ past: true, weekends: true }}
            dateFormat={DATE_FORMAT_TEXT}
          />
        </OmsFieldErrorWrapper>
      </div>
    </OrderRow>
  );
};
